import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, CheckCircle, AlertCircle, Code, Bot } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface ExamSettings {
  id: number;
  ai_grading_enabled: boolean;
  answer_mode: 'upload' | 'online';
  updated_at: string | null;
  updated_by: string | null;
}

export default function AnswerSettings() {
  const [settings, setSettings] = useState<ExamSettings>({
    id: 1,
    ai_grading_enabled: false,
    answer_mode: 'online',
    updated_at: null,
    updated_by: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 加载当前设置
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('exam_settings')
        .select('*')
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        return;
      }

      if (data) {
        setSettings({
          id: data.id,
          ai_grading_enabled: data.ai_grading_enabled ?? false,
          answer_mode: data.answer_mode as 'upload' | 'online',
          updated_at: data.updated_at,
          updated_by: data.updated_by,
        });
      }
    } catch (err) {
      // 静默处理错误
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const { data, error } = await supabase
        .from('exam_settings')
        .update({
          ai_grading_enabled: settings.ai_grading_enabled,
          answer_mode: settings.answer_mode,
          updated_at: new Date().toISOString(),
        })
        .eq('id', settings.id)
        .select();

      if (error) {
        setMessage({ type: 'error', text: '保存失败: ' + error.message });
        return;
      }

      if (!data || data.length === 0) {
        setMessage({ type: 'error', text: '保存失败：可能被权限策略拦截' });
        return;
      }

      setMessage({ type: 'success', text: '设置已保存' });
      // 3秒后清除消息
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: '保存异常，请重试' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">答题设置</h1>
          </div>
          <p className="text-gray-500 ml-13">配置AI阅卷开关和答题方式，影响编程题、Scratch编程题的评分流程和学员端答题界面</p>
        </div>

        {/* 消息提示 */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </motion.div>
        )}

        {/* AI阅卷开关 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Bot className="w-4 h-4 text-indigo-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">AI阅卷</h2>
            </div>
            <p className="text-gray-500 text-sm mt-2 ml-11">控制编程题、Scratch编程题等非客观题的AI自动评分功能</p>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900">AI自动评分</span>
                  {settings.ai_grading_enabled ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      已开启
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">
                      已关闭
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm">
                  {settings.ai_grading_enabled
                    ? '学员提交编程题后，系统将自动进行AI评分并给出反馈'
                    : '关闭后，刷题平台的非客观题（编程题、Scratch编程题等）将不可作答'}
                </p>
              </div>
              {/* Toggle Switch */}
              <button
                onClick={() => setSettings({ ...settings, ai_grading_enabled: !settings.ai_grading_enabled })}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  settings.ai_grading_enabled ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                    settings.ai_grading_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* 关闭时的警告提示 */}
            {!settings.ai_grading_enabled && (
              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">AI阅卷关闭后的影响：</p>
                    <ul className="space-y-1 text-amber-700 mt-1">
                      <li>• 刷题平台：非客观题不可作答，学员无法提交编程题答案</li>
                      <li>• 我的作业：不受此开关影响，学员始终可以作答</li>
                      <li>• 教师发布作业时，AI评卷选项将显示为"维护中"</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 非客观题答题方式设置 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Code className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">非客观题答题方式设置</h2>
            </div>
            <p className="text-gray-500 text-sm mt-2 ml-11">该设置将决定学员端答题界面的展示形式</p>
          </div>

          <div className="p-6 space-y-4">
            {/* 上传文件选项 */}
            <div
              onClick={() => setSettings({ ...settings, answer_mode: 'upload' })}
              className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.answer_mode === 'upload'
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    settings.answer_mode === 'upload'
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-gray-300'
                  }`}
                >
                  {settings.answer_mode === 'upload' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">上传文件</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                      学员上传代码文件/截图
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">
                    学员上传代码文件(.py/.cpp)或截图(.png/.jpg)，适用于线下编写后提交的场景
                  </p>
                </div>
              </div>
            </div>

            {/* 在线答题选项 */}
            <div
              onClick={() => setSettings({ ...settings, answer_mode: 'online' })}
              className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                settings.answer_mode === 'online'
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    settings.answer_mode === 'online'
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-gray-300'
                  }`}
                >
                  {settings.answer_mode === 'online' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">在线答题</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                      在系统内嵌编辑器中直接编写代码
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">
                    学员在系统内嵌的代码编辑器中直接编写代码，支持实时运行和调试
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 说明信息 */}
        <div className="bg-blue-50 rounded-xl p-5 border border-blue-100 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-blue-600 text-xs font-bold">i</span>
            </div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">说明：</p>
              <ul className="space-y-1 text-blue-700">
                <li>• AI阅卷开关为全局设置，影响所有试卷的刷题平台答题行为</li>
                <li>• 刷题平台：AI阅卷关闭时，非客观题不可作答；开启时，支持AI自动评分</li>
                <li>• 我的作业：不受AI阅卷开关影响，学员始终可以作答</li>
                <li>• 答题方式设置对新创建的试卷生效，已发布的试卷不受此设置影响</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="flex justify-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-200"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>保存中...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>保存设置</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}